import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileDoneOutlined,
  FileTextOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { Alert, Button, Card, Descriptions, Result, Space, Steps, Tag, Typography } from 'antd';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { APP_ROUTE_PREFIX, RouteConstant } from 'routes/routesConstant';
import { getSelectedTenant, saveSelectedTenant } from 'services/tenant/tenantMembershipService';

const stages = ['review', 'confirm', 'invoice', 'manage'];
const stageItems = [
  { title: 'Review plan', icon: <FileDoneOutlined /> },
  { title: 'Confirm order', icon: <SafetyCertificateOutlined /> },
  { title: 'Invoice & access', icon: <FileTextOutlined /> },
];

const dateLabel = (value) => new Date(value).toLocaleDateString('en-GB', {
  day: '2-digit', month: 'short', year: 'numeric',
});

export default function CommercialFlowPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialWorkspace = useMemo(() => getSelectedTenant() || {}, []);
  const [workspace, setWorkspace] = useState(initialWorkspace);
  const requestedStage = searchParams.get('stage');
  const stage = stages.includes(requestedStage) ? requestedStage : 'review';
  const currentStep = stage === 'review' ? 0 : stage === 'confirm' ? 1 : 2;
  const planMissing = !workspace.plan || workspace.plan === 'Plan not selected';
  const invoiceNumber = workspace.invoiceNumber || 'Created after order confirmation';

  const goToStage = (nextStage) => setSearchParams({ stage: nextStage });
  const returnToDashboard = () => navigate(`${APP_ROUTE_PREFIX}/${RouteConstant.dashboard}`);

  const issueInvoice = () => {
    const issuedAt = new Date();
    const accessEnd = new Date(issuedAt.getTime() + (14 * 24 * 60 * 60 * 1000));
    const updated = {
      ...workspace,
      subscriptionStatus: 'INVOICE_PENDING',
      paymentStatus: 'PENDING_PAYMENT',
      accessStatus: 'TEMPORARY_ACCESS',
      status: 'temporary-access',
      invoiceNumber: `INV-DEMO-${issuedAt.getFullYear()}-${String(issuedAt.getTime()).slice(-6)}`,
      invoiceIssuedAt: issuedAt.toISOString(),
      paymentDueAt: accessEnd.toISOString(),
      temporaryAccessEndAt: accessEnd.toISOString(),
    };
    saveSelectedTenant(updated);
    setWorkspace(updated);
    goToStage('invoice');
  };

  const approvePayment = () => {
    const updated = {
      ...workspace,
      subscriptionStatus: 'ACTIVE',
      paymentStatus: 'PAID',
      accessStatus: 'FULL_ACCESS',
      status: 'active',
      paidAt: new Date().toISOString(),
      temporaryAccessEndAt: null,
      paymentDueAt: null,
    };
    saveSelectedTenant(updated);
    setWorkspace(updated);
    goToStage('manage');
  };

  if (planMissing) {
    return (
      <div className="commercial-flow-page">
        <Card className="commercial-flow-card">
          <Result
            status="info"
            title="Choose a plan first"
            subTitle="You can return to the Dashboard afterward; organization setup does not need to be completed first."
            extra={<Space wrap><Button onClick={returnToDashboard}>Back to Dashboard</Button><Button type="primary" onClick={() => navigate('/onboarding?mode=change-plan')}>Choose a plan</Button></Space>}
          />
        </Card>
      </div>
    );
  }

  const header = (
    <>
      <div className="commercial-flow-header">
        <div><Typography.Text className="onboarding-eyebrow">PLAN &amp; BILLING</Typography.Text><Typography.Title level={2}>Complete billing when you are ready</Typography.Title><Typography.Paragraph type="secondary">Your organization setup and Dashboard remain available throughout this process.</Typography.Paragraph></div>
        <Button icon={<ArrowLeftOutlined />} onClick={returnToDashboard}>Back to Dashboard</Button>
      </div>
      <Steps current={currentStep} items={stageItems} responsive />
    </>
  );

  if (stage === 'manage') {
    return (
      <div className="commercial-flow-page"><Card className="commercial-flow-card">{header}
        <Result status="success" title="Payment confirmed — full access is active" subTitle="This demo organization now represents a paid subscription." />
        <Descriptions bordered column={{ xs: 1, md: 2 }}>
          <Descriptions.Item label="Plan">{workspace.plan}</Descriptions.Item>
          <Descriptions.Item label="Subscription"><Tag color="green">Active</Tag></Descriptions.Item>
          <Descriptions.Item label="Billing"><Tag color="green">Paid</Tag></Descriptions.Item>
          <Descriptions.Item label="Access"><Tag color="green">Full access</Tag></Descriptions.Item>
        </Descriptions>
        <div className="commercial-flow-actions"><Button onClick={() => goToStage('invoice')}>View invoice status</Button><Button type="primary" onClick={returnToDashboard}>Return to Dashboard <ArrowRightOutlined /></Button></div>
      </Card></div>
    );
  }

  return (
    <div className="commercial-flow-page"><Card className="commercial-flow-card">{header}
      {stage === 'review' ? (
        <section className="commercial-flow-section">
          <Alert showIcon type="info" message="No payment is required at this step" description="Review the saved plan. You may return to organization setup without confirming the order." />
          <Typography.Title level={3}>Review selected plan</Typography.Title>
          <Descriptions bordered column={{ xs: 1, md: 2 }}>
            <Descriptions.Item label="Organization">{workspace.name || 'New WorkPlansIQ Organization'}</Descriptions.Item>
            <Descriptions.Item label="Plan"><strong>{workspace.plan}</strong></Descriptions.Item>
            <Descriptions.Item label="Plan status"><Tag color="blue">Draft</Tag></Descriptions.Item>
            <Descriptions.Item label="Billing"><Tag>Not started</Tag></Descriptions.Item>
            <Descriptions.Item label="Access">Exploration</Descriptions.Item>
            <Descriptions.Item label="Setup">Can continue independently</Descriptions.Item>
          </Descriptions>
          <div className="commercial-flow-actions"><Button onClick={returnToDashboard}>Confirm later</Button><Button type="primary" onClick={() => goToStage('confirm')}>Continue to order <ArrowRightOutlined /></Button></div>
        </section>
      ) : null}

      {stage === 'confirm' ? (
        <section className="commercial-flow-section">
          <Alert showIcon type="warning" message="Confirming creates the invoice" description="The 14-day temporary-access period starts only after the invoice is issued." />
          <Typography.Title level={3}>Confirm subscription order</Typography.Title>
          <Descriptions bordered column={1}>
            <Descriptions.Item label="Organization">{workspace.name}</Descriptions.Item>
            <Descriptions.Item label="Selected plan">{workspace.plan}</Descriptions.Item>
            <Descriptions.Item label="Invoice">{invoiceNumber}</Descriptions.Item>
            <Descriptions.Item label="Temporary access">14 days from invoice issue date</Descriptions.Item>
            <Descriptions.Item label="During temporary access">Organization setup and included plan features remain available</Descriptions.Item>
          </Descriptions>
          <div className="commercial-flow-actions"><Button icon={<ArrowLeftOutlined />} onClick={() => goToStage('review')}>Back</Button><Button type="primary" icon={<FileTextOutlined />} onClick={issueInvoice}>Confirm order &amp; issue invoice</Button></div>
        </section>
      ) : null}

      {stage === 'invoice' ? (
        <section className="commercial-flow-section">
          <Alert showIcon type="success" message="14-day temporary access is active" description="The user can continue creating the organization, divisions, departments, positions, and working calendar while payment is pending." />
          <Typography.Title level={3}>Invoice and access status</Typography.Title>
          <Descriptions bordered column={{ xs: 1, md: 2 }}>
            <Descriptions.Item label="Invoice">{workspace.invoiceNumber || invoiceNumber}</Descriptions.Item>
            <Descriptions.Item label="Invoice status"><Tag color="gold">Pending payment</Tag></Descriptions.Item>
            <Descriptions.Item label="Issued">{workspace.invoiceIssuedAt ? dateLabel(workspace.invoiceIssuedAt) : 'Demo invoice not issued yet'}</Descriptions.Item>
            <Descriptions.Item label="Payment due">{workspace.paymentDueAt ? dateLabel(workspace.paymentDueAt) : 'Not available'}</Descriptions.Item>
            <Descriptions.Item label="Access"><Tag color="cyan" icon={<ClockCircleOutlined />}>Temporary access</Tag></Descriptions.Item>
            <Descriptions.Item label="Data protection">Data remains available if access becomes read-only</Descriptions.Item>
          </Descriptions>
          <div className="commercial-flow-demo-note"><CheckCircleOutlined /><span><strong>Demo control</strong>Use the payment approval button to demonstrate the transition from invoice pending to full access.</span></div>
          <div className="commercial-flow-actions"><Button onClick={returnToDashboard}>Continue setup while pending</Button><Button type="primary" icon={<CheckCircleOutlined />} onClick={approvePayment}>Simulate payment approved</Button></div>
        </section>
      ) : null}
    </Card></div>
  );
}
